import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { environment } from '@workspace/ng-config';
import { UserStore } from '@workspace/ng-data-access';
import { ApiService } from '@workspace/ng-http';
import { catchError, of, tap } from 'rxjs';

@Component({
  selector: 'aura-stripe-pricing-table',
  imports: [MatProgressSpinnerModule],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './stripe-pricing-table.component.html',
  styleUrls: ['./stripe-pricing-table.component.css'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StripePricingTableComponent implements OnInit {
  private document = inject(DOCUMENT);
  private userStore = inject(UserStore);
  private apiService = inject(ApiService);
  private destroyRef = inject(DestroyRef);

  pricingTableId = input(environment.stripe.pricingTableId);
  publishableKey = input(environment.stripe.publishableKey);

  readonly user = this.userStore.current;
  readonly customerSessionClientSecret = signal<string | null>(null);

  readonly isReady = computed(() => {
    const u = this.user();
    if (!u) return false;
    return this.customerSessionClientSecret() !== null;
  });

  constructor() {
    effect(() => {
      const u = this.user();
      if (u && !this.customerSessionClientSecret()) {
        this.loadCustomerSession();
      }
    });
  }

  ngOnInit() {
    this.loadStripeScript();
  }

  private loadCustomerSession() {
    this.apiService
      .user()
      .subscription()
      .createCustomerSession()
      .pipe(
        tap((res) => {
          if (res.clientSecret) {
            this.customerSessionClientSecret.set(res.clientSecret);
          }
        }),
        catchError((err) => {
          console.error('Failed to load Stripe Customer Session:', err);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private loadStripeScript() {
    if (this.document.getElementById('stripe-pricing-table-script')) {
      return;
    }
    const script = this.document.createElement('script');
    script.id = 'stripe-pricing-table-script';
    script.src = 'https://js.stripe.com/v3/pricing-table.js';
    script.async = true;
    this.document.head.appendChild(script);
  }
}
